import { useState, useEffect } from 'react';
import { Bug, CheckCircle, Clock, AlertTriangle, XCircle, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGES = {
  open: { icon: Clock, color: 'text-yellow-400 bg-yellow-400/10', label: 'Open' },
  in_progress: { icon: AlertTriangle, color: 'text-cyan bg-cyan/10', label: 'In Progress' },
  resolved: { icon: CheckCircle, color: 'text-emerald-400 bg-emerald-400/10', label: 'Resolved' },
  closed: { icon: XCircle, color: 'text-slate-500 bg-slate-500/10', label: 'Closed' },
};

const SEV_COLORS = { low: 'bg-slate-500', medium: 'bg-yellow-500', high: 'bg-orange-500', critical: 'bg-red-500' };

export default function AdminBugReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [respondDialog, setRespondDialog] = useState(null);
  const [response, setResponse] = useState('');
  const [newStatus, setNewStatus] = useState('');

  const token = localStorage.getItem('verbnotes_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchReports = async () => {
    try {
      const url = filter ? `${API}/admin/bug-reports?status=${filter}` : `${API}/admin/bug-reports`;
      const res = await axios.get(url, { headers });
      setReports(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReports(); }, [filter]);

  const handleUpdate = async () => {
    try {
      await axios.put(`${API}/admin/bug-reports/${respondDialog.id}`, {
        status: newStatus || respondDialog.status,
        admin_response: response,
      }, { headers });
      toast.success('Report updated');
      setRespondDialog(null);
      setResponse('');
      setNewStatus('');
      fetchReports();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  if (loading) return <div className="text-cyan animate-pulse text-center py-12">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="admin-bug-reports">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl text-white flex items-center gap-2"><Bug className="text-cyan" size={22} /> Bug Reports</h1>
          <p className="text-slate-400 text-sm">{reports.length} reports</p>
        </div>
        <div className="flex gap-1">
          {['', 'open', 'in_progress', 'resolved', 'closed'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filter === f ? 'bg-cyan/20 text-cyan' : 'text-slate-500 hover:text-slate-300'}`}
              data-testid={`filter-${f || 'all'}`}
            >
              {f || 'All'}
            </button>
          ))}
        </div>
      </div>

      {reports.length === 0 ? (
        <Card className="bg-navy-900/40 border-white/5"><CardContent className="p-8 text-center text-slate-500">No bug reports found.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {reports.map(r => {
            const st = STATUS_BADGES[r.status] || STATUS_BADGES.open;
            const StIcon = st.icon;
            return (
              <Card key={r.id} className="bg-navy-900/40 border-white/5 hover:border-white/10 transition-colors" data-testid={`bug-${r.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className={`w-2 h-2 rounded-full ${SEV_COLORS[r.severity] || SEV_COLORS.medium}`} />
                        <h3 className="text-white text-sm font-medium truncate">{r.title}</h3>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${st.color}`}>
                          <StIcon size={10} /> {st.label}
                        </span>
                      </div>
                      <p className="text-slate-400 text-xs mb-2 line-clamp-2">{r.description}</p>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span>{r.user_email}</span>
                        <span>{new Date(r.created_at).toLocaleDateString()}</span>
                        {r.page_url && <span className="truncate max-w-[200px]">{r.page_url}</span>}
                      </div>
                      {r.admin_response && (
                        <div className="mt-2 p-2 rounded-lg bg-cyan/5 border border-cyan/10 text-xs text-slate-300">
                          <span className="text-cyan font-medium">Admin: </span>{r.admin_response}
                        </div>
                      )}
                    </div>
                    <Button size="sm" variant="ghost" className="text-slate-400 hover:text-cyan" onClick={() => { setRespondDialog(r); setNewStatus(r.status); setResponse(r.admin_response || ''); }} data-testid={`respond-${r.id}`}>
                      <MessageSquare size={14} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!respondDialog} onOpenChange={() => setRespondDialog(null)}>
        <DialogContent className="bg-navy-900 border-white/10 text-white">
          <DialogHeader><DialogTitle>Respond to: {respondDialog?.title}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-slate-400 text-xs block mb-1">Description</label>
              <p className="text-slate-300 text-sm">{respondDialog?.description}</p>
            </div>
            {respondDialog?.steps_to_reproduce && (
              <div>
                <label className="text-slate-400 text-xs block mb-1">Steps to Reproduce</label>
                <p className="text-slate-300 text-sm whitespace-pre-line">{respondDialog?.steps_to_reproduce}</p>
              </div>
            )}
            <div>
              <label className="text-slate-400 text-xs block mb-1">Status</label>
              <select value={newStatus} onChange={e => setNewStatus(e.target.value)} className="w-full bg-navy-950 border border-white/10 text-white rounded-lg px-3 py-2 text-sm" data-testid="bug-status-select">
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <label className="text-slate-400 text-xs block mb-1">Admin Response</label>
              <Textarea value={response} onChange={e => setResponse(e.target.value)} rows={3} placeholder="Your response..." className="bg-navy-950 border-white/10 text-white resize-none" data-testid="bug-response-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRespondDialog(null)} className="text-slate-400">Cancel</Button>
            <Button onClick={handleUpdate} className="bg-cyan text-navy-950" data-testid="bug-update-btn">Update Report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
